import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { selectPluginManifest } from 'domains/coursewareItem/selectors';
import { commandAuthorPluginGet } from 'containers/ConnectionManager/actions';
import { selectPluginSchema } from 'data/Plugin/selectors';

/**
 * Provides plugin data to a wrapped component.
 * @param {function(*): string} getCourseware A function used to assert the identity
 *   of a courseware from the component's properties.  Re-runs every time the
 *   props change.  If the function returns falsy, then the placeholder or null will
 *   be rendered.
 * @param {React.Component} PlaceholderComponent An optional component to use
 *   when the plugin isn't yet available.
 */
export default (getCourseware, PlaceholderComponent) => (Component) => {
  class EnhancedComponent extends React.Component {
    static displayName = `EnhanceWithPlugin(${Component.displayName || Component.name || 'Component'})`;

    // fetch the plugin on mount…
    UNSAFE_componentWillMount() {
      const { pluginManifest, pluginSchema } = this.props;

      if (!(pluginManifest && pluginSchema)) {
        const courseware = getCourseware(this.props);
        if (courseware) {
          const { plugin: pluginSummary } = courseware;
          this.props.loadPlugin(pluginSummary);
        }
      }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
      const courseware = getCourseware(this.props);
      const nextCourseware = getCourseware(nextProps);

      // Make sure the plugin updates when the courseware
      // changes.
      if (courseware && nextCourseware) {
        const { plugin: pluginSummary } = courseware;
        const { plugin: nextPluginSummary } = nextCourseware;
        if (
          // Plugin identity is asserted by both the plugin id and
          // the plugin version.
          pluginSummary.pluginId !== nextPluginSummary.pluginId ||
          pluginSummary.version !== nextPluginSummary.version
        ) {
          this.props.loadPlugin(nextPluginSummary);
        }
      }
    }

    render() {
      const { pluginManifest, pluginSchema } = this.props;

      if (pluginManifest && pluginSchema) {
        return <Component {...this.props} />;
      }
      return (PlaceholderComponent && <PlaceholderComponent />) || null;
    }
  }

  EnhancedComponent.propTypes = {
    /* [Plugin Manifest](https://dev-developers.smartsparrow.com/plugins/advanced/manifest/) */
    pluginManifest: PropTypes.object,
    /* [Plugin Schema](https://dev-developers.smartsparrow.com/plugins/schemas/overview/) */
    pluginSchema: PropTypes.object,
    /* function to load a plugin given a plugin summary */
    loadPlugin: PropTypes.func.isRequired,
  };

  const mapStateToProps = (state, ownProps) => {
    const updatedProps = {};
    const courseware = getCourseware(ownProps);
    if (courseware) {
      const pluginSummary = courseware.plugin;
      updatedProps.pluginManifest = selectPluginManifest(state, pluginSummary.pluginId, pluginSummary.version);
      updatedProps.pluginSchema = selectPluginSchema(state, pluginSummary.pluginId);
    }
    return updatedProps;
  };

  const mapDispatchToProps = (dispatch) => ({
    loadPlugin: (pluginSummary) => dispatch(
      commandAuthorPluginGet(pluginSummary.pluginId, pluginSummary.version)
    ),
  });

  const connectedComponent = connect(mapStateToProps, mapDispatchToProps)(EnhancedComponent);
  return hoistNonReactStatics(connectedComponent, Component);
};
